import { useContext } from 'react'

import {
  useForm, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import * as providerProductCodeActions from '@redux/modules/providerProductCode'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/types'

import PageContext from '@contexts/pageContext'

import useSelector from '@hooks/useSelector'

import defaultState, { requiredFields } from '@models/providerProductCode'
import type { ProviderModel, ProviderProductCodeModel, ServiceProductModel } from '@models/types'

export const generateUrls = () => ({ providerProductCodesIndexUrl: '#/systemManager/providerProductCodes' })

type CreateProviderProductCodePayload = {
  dispatch: AppDispatch,
  providerProductCodeParams: Partial<ProviderProductCodeModel>,
  requestOptions?: EntityOptions,
}

const createProviderProductCode = (params: CreateProviderProductCodePayload) => {
  const { providerProductCodeParams, dispatch, requestOptions = {} } = params
  const { createProviderProductCode: createFn } = providerProductCodeActions

  return dispatch(createFn(providerProductCodeParams, requestOptions))
}

type UpdateProviderProductCodePayload = {
  dispatch: AppDispatch,
  providerProductCodeParams: Partial<ProviderProductCodeModel>,
  requestOptions?: EntityOptions,
}

const updateProviderProductCode = (params: UpdateProviderProductCodePayload) => {
  const { providerProductCodeParams, dispatch, requestOptions = {} } = params
  const { updateProviderProductCode: updateFn } = providerProductCodeActions

  return dispatch(updateFn(providerProductCodeParams, requestOptions))
}

type DeleteProviderProductCodePayload = {
  dispatch: AppDispatch,
  providerProductCode: Partial<ProviderProductCodeModel>,
}

const deleteProviderProductCode = (params: DeleteProviderProductCodePayload) => {
  const { providerProductCode, dispatch } = params
  const { deleteProviderProductCode: deleteFn } = providerProductCodeActions

  return dispatch(deleteFn(providerProductCode))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useProviderProductCodeForm(
  providerProductCode: Partial<ProviderProductCodeModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
): UseFormPayload {
  const { validateOn } = options

  const providerProductCodeForm = useForm(
    defaultState,
    { entity: providerProductCode, requiredFields, validateOn },
    [providerProductCode.id],
  )

  return {
    ...providerProductCodeForm,
  }
}

export function useRelations(providerProductCode: ProviderProductCodeModel) {
  const { provider_id, service_product_id } = providerProductCode

  const { providers, serviceProducts } = useSelector(reduxState => reduxState.entities)

  const provider: Partial<ProviderModel> = provider_id && providers[provider_id] ? providers[provider_id] : {}
  const serviceProduct: Partial<ServiceProductModel> = service_product_id && serviceProducts[service_product_id]
    ? serviceProducts[service_product_id] : {}

  return {
    provider,
    serviceProduct,
  }
}

function useProviderProductCode(initEntity = {}) {
  // @ts-expect-error TS2345
  const { entity: providerProductCode } = useLatestEntity(initEntity, 'providerProductCodes')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, updating,
  } = useSelector(reduxState => reduxState.providerProductCodes)

  // @ts-expect-error TS2345
  const { provider, serviceProduct } = useRelations(providerProductCode)

  return {
    callbacks: {
      createOrEditProviderProductCode: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditProviderProductCodeModal',
        payload: { providerProductCode },
      }),
      createProviderProductCode: (
        providerProductCodeParams: Partial<ProviderProductCodeModel>,
        requestOptions: EntityOptions,
      ) => createProviderProductCode({ dispatch, providerProductCodeParams, requestOptions }),
      // @ts-expect-error TS2322
      deleteProviderProductCode: () => deleteProviderProductCode({ providerProductCode, dispatch }),
      updateProviderProductCode: (
        providerProductCodeParams: Partial<ProviderProductCodeModel>,
        requestOptions: EntityOptions,
      ) => updateProviderProductCode({ providerProductCodeParams, dispatch, requestOptions }),
    },
    creating,
    deleting,
    provider,
    providerProductCode,
    serviceProduct,
    updating,
    urls: generateUrls(),
  }
}

export default useProviderProductCode
